import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import AdminUserList from './AdminUserList';
import AdminInviteCodeManager from './AdminInviteCodeManager';
import AdminActivityLog from './AdminActivityLog';
import AdminPendingReview from './AdminPendingReview';
import api from '../../services/api';

const AdminDashboard = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const [activeTab, setActiveTab] = useState('pending');
  const [stats, setStats] = useState(null);
  const { user } = useAuth();
  
  useEffect(() => {
    fetchStats();
  }, []);
  
  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      // Stats loaded
    }
  };
  
  // Check if user has admin role
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  
  
  if (!isAdmin) {
    return <Navigate to="/dashboard" />;
  }
  
  const endImpersonation = () => {
    // Clear impersonation token and restore original
    const originalToken = localStorage.getItem('originalToken');
    if (originalToken) {
      localStorage.setItem('accessToken', originalToken);
      localStorage.removeItem('originalToken');
      localStorage.removeItem('impersonationToken');
      window.location.href = '/admin';
    }
  };
  
  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header with Impersonation Indicator */}
      {user?.isImpersonation && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700/40 p-4 mb-6 flex justify-between items-center rounded-lg">
          <span className="text-mono">
            {t('admin.dashboard.impersonating', { email: user.email, byEmail: user.impersonatedByEmail })}
          </span>
          <button onClick={endImpersonation} className="bg-white dark:bg-[#1f1f1f] border border-gray-300 dark:border-[#2e2e2e] hover:bg-gray-50 dark:hover:bg-[#262626] text-sm px-4 py-2">
            {t('admin.dashboard.endImpersonation')}
          </button>
        </div>
      )}
      
      <div className="mb-8">
        <h1 className={`heading-mono text-hierarchy-1 mb-2 text-gray-800 dark:text-[#f5f5f5]`}>
          {t('admin.dashboard.title')}
        </h1>
        <p className={`text-mono text-gray-600 dark:text-[#c4c4c4]`}>
          {t('admin.dashboard.subtitle')}
        </p>
      </div>
      
      {/* Quick Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white dark:bg-[#1f1f1f] border border-gray-200 dark:border-[#2e2e2e] rounded-lg p-4">
            <h3 className="text-mono text-sm text-gray-400 dark:text-[#999999] mb-1">{t('admin.dashboard.stats.totalUsers')}</h3>
            <p className="text-2xl font-bold text-white">{stats.total_users}</p>
          </div>
          <div className="bg-white dark:bg-[#1f1f1f] border border-gray-200 dark:border-[#2e2e2e] rounded-lg p-4">
            <h3 className="text-mono text-sm text-gray-400 dark:text-[#999999] mb-1">{t('admin.dashboard.stats.activeUsers')}</h3>
            <p className="text-2xl font-bold text-green-400">{stats.active_users_7d}</p>
          </div>
          <div className="bg-white dark:bg-[#1f1f1f] border border-gray-200 dark:border-[#2e2e2e] rounded-lg p-4">
            <h3 className="text-mono text-sm text-gray-400 dark:text-[#999999] mb-1">{t('admin.dashboard.stats.totalSearches')}</h3>
            <p className="text-2xl font-bold text-accent-400">{stats.total_searches}</p>
          </div>
          <div className="bg-white dark:bg-[#1f1f1f] border border-gray-200 dark:border-[#2e2e2e] rounded-lg p-4">
            <h3 className="text-mono text-sm text-gray-400 dark:text-[#999999] mb-1">{t('admin.dashboard.stats.activeCodes')}</h3>
            <p className="text-2xl font-bold text-yellow-400">{stats.active_invite_codes}</p>
          </div>
        </div>
      )}
      
      {/* Tab Navigation */}
      <div className="bg-white dark:bg-[#1f1f1f] border border-gray-200 dark:border-[#2e2e2e] rounded-lg p-0 mb-6 overflow-hidden">
        <div className="flex">
          {['pending', 'users', 'invites', 'activity'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-4 text-mono capitalize transition-all ${
                activeTab === tab 
                  ? 'bg-accent-400/10 border-b-2 border-accent-400 text-accent-400' 
                  : 'text-gray-600 dark:text-[#c4c4c4] hover:text-gray-800 dark:hover:text-[#f5f5f5]'
              }`}
            >
              {t(`admin.dashboard.tabs.${tab}`)}
            </button>
          ))}
        </div>
      </div>
      
      {/* Tab Content */}
      <div className="animate-fade-in-scale">
        {activeTab === 'pending' && <AdminPendingReview />}
        {activeTab === 'users' && <AdminUserList />}
        {activeTab === 'invites' && <AdminInviteCodeManager />}
        {activeTab === 'activity' && <AdminActivityLog />}
      </div>
    </div>
  );
};

export default AdminDashboard;